// src/Payroll_Management/PayRoll_Report/RemarksView.jsx
import { useEffect, useContext, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";

import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { ArrowLeft, Eye, FileText } from "lucide-react";

const BASE_URL = "https://nissi-india.com";

function s(v) {
  return (v ?? "").toString().trim();
}
function toInt(v, def = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
}

const RemarksView = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const employeeId = searchParams.get("employeeId") || "";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const screenBadge = useMemo(() => "INFRA SCREEN : C-46", []);

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-4 py-2 rounded-2xl text-white text-sm font-bold shadow ` +
    `transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const fetchList = async (p = page) => {
    if (!s(finYearId)) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!s(employeeId)) {
      toast.error("employeeId missing in URL");
      return;
    }

    try {
      setLoading(true);

      const res = await axios.get(
        `${BASE_URL}/api/performance-remarks-report/remarks-list`,
        {
          params: {
            finYearId: Number(finYearId),
            employeeId: Number(employeeId),
          },
        }
      );

      if (!res?.data?.success) {
        toast.error(res?.data?.message || "Failed to load remarks");
        return;
      }

      const allRows = res.data.rows || [];
      const totalCount = allRows.length;

      const start = (p - 1) * pageSize;
      const end = start + pageSize;

      setRows(allRows.slice(start, end));
      setTotal(totalCount);
      setTotalPages(Math.ceil(totalCount / pageSize));
      setPage(p);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList(1);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchList(page);
    // eslint-disable-next-line
  }, [page]);

  const openRemark = () => {
    window.open(
      `/PayRoll/RemarksReportView?employeeId=${employeeId}`,
      "_blank"
    );
  };

  // 5 page window pagination
  const getVisiblePages = () => {
    const windowSize = 5;
    const start = Math.max(1, page - 2);
    const end = Math.min(totalPages, start + windowSize - 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, total);

  return (
    <PayRollLayoutWrapper>
      <ToastContainer theme={isDark ? "dark" : "light"} />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">EMPLOYEE REMARKS</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-4">
              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"}`}>
                {screenBadge}
              </div>
              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"}`}>
                {total} Remarks
              </div>
            </div>

            <button onClick={() => navigate(-1)} className={ghostBtn}>
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
          </div>

          {/* Table */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center">S.No</th>
                    <th className="px-4 py-4 text-center">Employee Code</th>
                    <th className="px-4 py-4 text-center">Employee Name</th>
                    <th className="px-4 py-4 text-center">Date</th>
                    <th className="px-4 py-4 text-center">Remarks</th>
                    <th className="px-4 py-4 text-center">View</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="text-center py-8">
                        Loading...
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-10">
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => (
                      <tr key={idx} className="hover:bg-gray-100 dark:hover:bg-gray-800">
                        <td className="text-center py-3">
                          {(page - 1) * pageSize + idx + 1}
                        </td>
                        <td className="text-center">{r.fld_Employeecode}</td>
                        <td className="text-center">{r.fld_Employee}</td>
                        <td className="text-center">{r.RemarkDate}</td>
                        <td className="text-center truncate max-w-[250px]">
                          {r.fld_Details}
                        </td>
                        <td className="text-center">
                          <button onClick={openRemark} className={primaryBtn}>
                            <Eye className="w-4 h-4" />
                            View
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-between items-center mt-6">
              <div>
                Showing <b>{showingFrom}</b> to <b>{showingTo}</b> of <b>{total}</b>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 border rounded"
                >
                  Prev
                </button>

                {getVisiblePages().map((p) => (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`px-3 py-1 rounded ${
                      p === page ? `${primaryBtnClass} text-white` : "border"
                    }`}
                  >
                    {p}
                  </button>
                ))}

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-1 border rounded"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default RemarksView;
